import React, { useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { 
  FiDollarSign, 
  FiUsers, 
  FiClock, 
  FiBarChart,
  FiSettings,
  FiActivity,
  FiTrendingUp
} from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { usePlanRestriction } from "../../context/PlanRestrictionContext";
import { useAccessControl } from "../../hooks/useAccessControl";

// Import tab components
import PayrollDashboard from "./tabs/PayrollDashboard";
import PaymentTracking from "./tabs/PaymentTracking";
import PaymentFlowDetails from "./tabs/PaymentFlowDetails";

const EnhancedPayrollManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useUserContext();
  // Feature access is now only restricted by user role (branch vs main branch)
  // All Main Branch users have access to Enhanced Payroll regardless of plan
  const {
    canAccessPayroll,
    isPayrollAdmin,
    isHRManager,
    isAccountant,
    isPayrollViewer,
    canAccessPayrollFeature,
    getUserDisplayRole
  } = useAccessControl();
  
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Check if user can access payroll (Main Branch users only)
  if (!canAccessPayroll()) {
    return null; // Don't render anything if user can't access payroll
  }

  // Define available tabs based on user permissions
  const getAvailableTabs = () => {
    const tabs = [
      {
        name: "Dashboard",
        icon: <FiDollarSign size={16} />,
        component: <PayrollDashboard />,
        description: "Payroll overview and quick actions",
        canAccess: () => isPayrollAdmin() || isHRManager() || isAccountant() || isPayrollViewer()
      },
      {
        name: "Payment Tracking",
        icon: <FiClock size={16} />,
        component: <PaymentTracking />,
        description: "Monitor payment status and retry failed payments",
        canAccess: () => canAccessPayrollFeature('payment_tracking')
      },
      {
        name: "Payment Flow Details",
        icon: <FiTrendingUp size={16} />,
        component: <PaymentFlowDetails />,
        description: "Track detailed payment flow and settlement information",
        canAccess: () => canAccessPayrollFeature('payment_tracking')
      }
    ];

    // Filter tabs based on user permissions
    return tabs.filter(tab => tab.canAccess());
  };

  const availableTabs = getAvailableTabs();

  // Access control - only users with payroll roles can access
  if (!isPayrollAdmin() && !isHRManager() && !isAccountant() && !isPayrollViewer()) {
    return null; // Don't render anything if user doesn't have payroll roles
  }

  return (
    <Box p={6} bg={bgColor} minH="100vh">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" color="gray.900" mb={2}>
            Payroll Operations
          </Heading>
          <Text color="gray.600" fontSize="md">
            Comprehensive payroll management with multi-level approvals and international payments
          </Text>
        </Box>

        {/* User Role Info */}
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="lg"
          p={4}
        >
          <VStack spacing={2} align="start">
            <Text fontSize="sm" fontWeight="600" color="blue.900">
              Your Payroll Access:
            </Text>
            <Text fontSize="sm" color="blue.700">
              Role: {getUserDisplayRole()}
            </Text>
            <Text fontSize="sm" color="blue.700">
              Branch: {user?.branch_name || 'Unknown'}
            </Text>
            {user?.payroll_roles && Array.isArray(user.payroll_roles) && user.payroll_roles.length > 0 && (
              <Text fontSize="sm" color="blue.700">
                Payroll Roles: {user.payroll_roles.join(', ')}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Main Tabs */}
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          overflow="hidden"
        >
          <Tabs 
            index={activeTab} 
            onChange={setActiveTab}
            variant="enclosed"
            colorScheme="blue"
          >
            <TabList bg="gray.50">
              {availableTabs.map((tab, index) => (
                <Tab
                  key={index}
                  _selected={{ bg: "white", borderBottomColor: "white" }}
                  _hover={{ bg: "gray.100" }}
                  py={4}
                  px={6}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {tab.icon}
                    <Text fontWeight="500">{tab.name}</Text>
                  </Box>
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {availableTabs.map((tab, index) => (
                <TabPanel key={index} p={6}>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Heading size="md" color="gray.800" mb={2}>
                        {tab.name}
                      </Heading>
                      <Text color="gray.600" fontSize="sm">
                        {tab.description}
                      </Text>
                    </Box>
                    {tab.component}
                  </VStack>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>

    </Box>
  );
};

export default EnhancedPayrollManagement;